import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast, Slide } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useRef, useState } from 'react';
import { jwtDecode } from 'jwt-decode';

import { authApi } from '../../../core/api/auth.api';
import { useAuthProviderV2 } from '../../../providers/provider-hooks';
import { useRoutesProvider } from '../../../providers/provider-hooks';
import { updateSuccessToast } from '../../../toast';
import { delay } from '../../../shared/utils';
import {
  loginSchema,
  type LoginFormData,
} from '../validation/registration.schemas';
import type { UserInfo } from '../../../providers/auth-provider.types';
import { useLoginFailureEscalation } from './useLoginFailureEscalation';
import { resolvePostAuthTarget } from '../utils/resolve-post-auth-target';
import { pasiInscriereActive } from '../utils/pasi-inscriere';
import { getErrorMessage, getErrorStatus } from '@/core/api/response-handler';
import { applyServerFieldErrors } from '@/shared/utils';

const LAST_EMAIL_KEY = 'ecotic.last-email';

/**
 * useLoginWithForm - Login hook using react-hook-form + Zod
 *
 * Modern implementation with:
 * - react-hook-form for state management
 * - Zod for email/password validation
 * - shadcn Form components
 * - Server-side error handling
 *
 * Replaces: useLogin.ts (manual state management)
 */
export function useLoginWithForm() {
  const { updateToken, updateRefreshToken } = useAuthProviderV2();
  const navigate = useNavigate();
  const { getUserHomePage, PATHS, PAGES, ROLES } = useRoutesProvider();
  const escalation = useLoginFailureEscalation();
  const silentRetryUsed = useRef(false);

  // Afiseaza linkul catre reinscriere dupa un login esuat cu 401.
  const [showReenrollHint, setShowReenrollHint] = useState(false);

  // Eroarea de login se arata IN cardul «Conectare», langa buton, nu intr-un toast (directia S1,
  // 2026-09-15). Toastul sta fixat sus: la 360x649 acoperea titlul «Înregistrare» si era la 431px de
  // butonul apasat; mutat jos, acoperea chiar butonul (-42px). Restul toasturilor raman neschimbate.
  const [loginError, setLoginError] = useState<string | null>(null);

  // Initialize react-hook-form with Zod validation
  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: localStorage.getItem(LAST_EMAIL_KEY) ?? '',
      parola: '',
    },
    mode: 'onBlur', // Validate on blur, not on every keystroke
  });

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: async (data: LoginFormData) => {
      await delay(1000); // Artificial delay for UX
      silentRetryUsed.current = false;
      try {
        // Email/password already trimmed and transformed by Zod schema
        return await authApi.login(data);
      } catch (error: unknown) {
        // Silent retry: if 401 and password contains whitespace, retry without it
        if (getErrorStatus(error) === 401 && /\s/.test(data.parola)) {
          const trimmedParola = data.parola.replace(/\s/g, '');
          try {
            const result = await authApi.login({ ...data, parola: trimmedParola });
            silentRetryUsed.current = true;
            return result;
          } catch {
            // Silent retry also failed, throw original error
            throw error;
          }
        }
        throw error;
      }
    },
    onMutate: async () => {
      setLoginError(null);
      // Create loading toast at mutation start
      // Return toastId to make it accessible in onSuccess/onError callbacks via onMutateResult parameter
      const toastId = toast.loading('Autentificare în curs...', { transition: Slide });
      return { toastId };
    },
    onSuccess: (data, variables, onMutateResult) => {
      // Remember email for next login
      localStorage.setItem(LAST_EMAIL_KEY, variables.email);
      escalation.resetFailures();

      // Update toast to success
      if (onMutateResult?.toastId) {
        const message = silentRetryUsed.current
          ? 'Autentificare reușită! (Am eliminat spațiile din parolă automat)'
          : 'Autentificare reușită!';
        updateSuccessToast(message, onMutateResult.toastId);
      }

      // Update tokens in auth provider and navigate to user's home page
      // Note: tokens should always be present on successful login, but we check to satisfy TypeScript
      if (!data.token || !data.refreshToken) {
        throw new Error('Authentication response missing tokens');
      }

      const token = data.token;

      // Update tokens first
      updateRefreshToken(data.refreshToken);
      updateToken(token);

      // Decodam JWT-ul si navigam SINCRON, cu aceeasi functie ca restul aplicatiei.
      // Inainte era un `setTimeout(..., 50)` care se uita doar la `role`: fiind ultimul si in
      // afara lantului de efecte React, castiga mereu cursa cu logica pe stagii din
      // `useAuthRedirects`, deci un profesor cu cerere in asteptare ateriza pe dashboard.
      // `pathname: null` = „tocmai s-a logat, nu conteaza unde e acum" -> intoarce mereu o tinta.
      try {
        const decoded = jwtDecode<UserInfo>(token);
        const target =
          resolvePostAuthTarget(decoded, null, {
            PATHS,
            PAGES,
            ROLES,
            getUserHomePage,
            pasiInscriere: pasiInscriereActive(),
          }) ??
          getUserHomePage(decoded.role);
        navigate(target, { replace: true }); // replace: true prevents back button to login page
      } catch (error) {
        console.error('Failed to decode JWT token or navigate:', error);
        // Fallback: home-ul rolului necunoscut, nu o cale hardcodata
        navigate(getUserHomePage(), { replace: true });
      }
    },
    onError: (error, _variables, onMutateResult: any) => {
      console.error('Login error:', error);

      // Escalate on 401
      if (getErrorStatus(error) === 401) {
        escalation.incrementFailure();
        // Backendul intoarce 401 `account.not.in.edition` si cand emailul nu exista DELOC, nu
        // doar cand contul e din editia trecuta (AuthenticationService: acelasi orElseThrow).
        // De aceea NU redirectam automat spre reinscriere - un email tastat gresit ar trimite
        // omul intr-un flux gresit. Aratam doar un link, si decide el.
        setShowReenrollHint(true);
      }

      // Modern pattern: error.errors (transformed by response-handler)
      // Context7 Pattern: Map backend errors to form fields using react-hook-form
      applyServerFieldErrors(error, form.setError);

      // Update toast with error message - provide specific feedback based on status code
      let errorMessage = 'Autentificare eșuată. Verifică datele introduse.';

      // Check for specific HTTP status codes to provide better user feedback
      if (getErrorStatus(error) === 401) {
        // Backendul intoarce deja un mesaj generic, anti-enumerare ("Utilizatorul si parola sunt
        // incorecte."), fara emailul tastat. Compunerea unui mesaj propriu aici il arunca si il
        // inlocuieste cu unul care REPETA emailul -- exact ce anti-enumerarea backendului evita
        // (S1, constatarea 23, masurat pe DEV cu cont real).
        errorMessage = getErrorMessage(error, 'Email sau parolă incorectă.');
      } else if (getErrorStatus(error) === 429) {
        errorMessage = 'Prea multe încercări. Încearcă din nou în câteva minute.';
      } else if ((getErrorStatus(error) ?? 0) >= 500) {
        errorMessage = 'Eroare de server. Încearcă din nou mai târziu.';
      } else if (!getErrorStatus(error) && getErrorMessage(error, '').includes('Network')) {
        errorMessage = 'Eroare de conexiune. Verifică internetul.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      // Mesajul merge in card (`loginError`); toastul de incarcare doar se inchide.
      setLoginError(errorMessage);
      if (onMutateResult?.toastId) {
        toast.dismiss(onMutateResult.toastId);
      }
    },
  });

  // Form submit handler
  const handleSubmit = async (data: LoginFormData) => {
    try {
      await loginMutation.mutateAsync(data);
    } catch {
      // Error handling done in mutation onError callback
      // Toast update handled in onError via onMutateResult.toastId
    }
  };

  return {
    form,
    onSubmit: form.handleSubmit(handleSubmit),
    loading: loginMutation.isPending,
    escalation,
    showReenrollHint,
    loginError,
  };
}
